import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { WebPageSchema, BreadcrumbListSchema, ArticleSchema } from '@/components/feature/SchemaOrg';
import { articles } from '@/mocks/siteData';

const articleImages: Record<string, string> = {
  '1': 'https://readdy.ai/api/search-image?query=Luxury%20dark%20themed%20abstract%20illustration%20with%20golden%20trophy%20concept%2C%20elegant%20geometric%20patterns%20with%20warm%20amber%20accents%2C%20premium%20atmospheric%20composition%2C%20award%20celebration%20theme%2C%20cinematic%20lighting%2C%20dark%20background%20with%20gold%20highlights&width=1200&height=600&seq=article-award-01&orientation=landscape',
  '2': 'https://readdy.ai/api/search-image?query=Modern%20dark%20abstract%20cybersecurity%20illustration%20with%20golden%20shield%20concept%2C%20elegant%20geometric%20lock%20patterns%2C%20warm%20amber%20accents%20on%20dark%20background%2C%20premium%20corporate%20security%20theme%2C%20cinematic%20lighting&width=1200&height=600&seq=article-security-01&orientation=landscape',
  '3': 'https://readdy.ai/api/search-image?query=Elegant%20dark%20abstract%20illustration%20with%20golden%20gift%20box%20and%20celebration%20theme%2C%20luxurious%20atmospheric%20composition%20with%20warm%20amber%20particles%2C%20promotion%20and%20welcome%20bonus%20concept%2C%20cinematic%20lighting%20on%20dark%20background&width=1200&height=600&seq=article-promo-01&orientation=landscape',
  '4': 'https://readdy.ai/api/search-image?query=Modern%20dark%20abstract%20digital%20currency%20illustration%20with%20golden%20circuit%20patterns%2C%20blockchain%20network%20concept%2C%20elegant%20geometric%20design%20with%20amber%20accents%2C%20fintech%20payment%20theme%2C%20cinematic%20dark%20background&width=1200&height=600&seq=article-payment-01&orientation=landscape',
  '5': 'https://readdy.ai/api/search-image?query=Luxury%20dark%20abstract%20VIP%20membership%20illustration%20with%20golden%20crown%20concept%2C%20premium%20diamond%20geometric%20patterns%2C%20warm%20amber%20highlights%20on%20elegant%20dark%20background%2C%20exclusive%20club%20theme%2C%20cinematic%20lighting&width=1200&height=600&seq=article-vip-01&orientation=landscape',
  '6': 'https://readdy.ai/api/search-image?query=Elegant%20dark%20abstract%20illustration%20with%20balanced%20scales%20concept%2C%20golden%20harmony%20patterns%2C%20responsible%20and%20ethical%20theme%2C%20warm%20amber%20accents%20on%20premium%20dark%20background%2C%20corporate%20social%20responsibility&width=1200&height=600&seq=article-responsible-01&orientation=landscape',
};

export default function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-file-unknow-line text-6xl text-secondary-400 w-16 h-16 flex items-center justify-center mx-auto mb-4"></i>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-secondary-600 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              返回文章列表
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <WebPageSchema
        title={`${article.title} - 开云官网`}
        description={article.summary}
        url={`https://www.kaiyun.com/articles/${article.id}`}
      />
      <ArticleSchema
        article={{
          title: article.title,
          summary: article.summary,
          author: article.author,
          date: article.date,
          url: `https://www.kaiyun.com/articles/${article.id}`,
        }}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: 'https://www.kaiyun.com/' },
          { name: '最新资讯', url: 'https://www.kaiyun.com/articles' },
          { name: article.title, url: `https://www.kaiyun.com/articles/${article.id}` },
        ]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb
            items={[
              { label: '最新资讯', href: '/articles' },
              { label: article.title },
            ]}
          />

          <main>
            <article className="py-10 md:py-16">
              <div className="max-w-4xl mx-auto px-4 md:px-6">
                <div className="rounded-2xl overflow-hidden mb-8">
                  <img
                    src={articleImages[article.id]}
                    alt={article.title}
                    className="w-full h-64 md:h-80 object-cover object-top"
                  />
                </div>

                <div className="mb-8">
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-500/10 text-accent-600 whitespace-nowrap">
                      {article.categoryName}
                    </span>
                    <span className="text-sm text-secondary-500">{article.date}</span>
                    <span className="text-sm text-secondary-500">{article.readTime} 阅读</span>
                    <span className="text-sm text-secondary-500">作者：{article.author}</span>
                  </div>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                    {article.title}
                  </h1>
                  <p className="text-base md:text-lg text-secondary-600 leading-relaxed">
                    {article.summary}
                  </p>
                </div>

                <div className="border-t border-background-200/50 pt-8">
                  <div className="prose prose-lg max-w-none">
                    {article.content.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="text-foreground-800 leading-relaxed mb-5 text-base">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>

                <div className="mt-12 pt-8 border-t border-background-200/50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <Link
                    to="/articles"
                    className="inline-flex items-center gap-2 text-sm text-primary-500 font-medium hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                    返回文章列表
                  </Link>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-secondary-500">分享：</span>
                    <button className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-secondary-500 hover:text-primary-500 hover:bg-primary-500/10 transition-all duration-200 cursor-pointer" aria-label="复制链接">
                      <i className="ri-link w-4 h-4 flex items-center justify-center"></i>
                    </button>
                    <button className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-secondary-500 hover:text-primary-500 hover:bg-primary-500/10 transition-all duration-200 cursor-pointer" aria-label="分享到微信">
                      <i className="ri-wechat-line w-4 h-4 flex items-center justify-center"></i>
                    </button>
                  </div>
                </div>
              </div>
            </article>
          </main>
        </div>
        <Footer />
      </div>
    </>
  );
}